import * as React from "react";
import { cn } from "./utils";

type AlertVariant = "info" | "success" | "warn" | "warning" | "error";
type AlertProps = Omit<React.HTMLAttributes<HTMLDivElement>, "title"> & {
  title?: React.ReactNode;
  variant?: AlertVariant;
  icon?: React.ReactNode;
};

const alertStyles: Record<Exclude<AlertVariant, "warning">, string> = {
  info: "border-blue-200 bg-blue-50 text-blue-900",
  success: "border-green-200 bg-green-50 text-green-900",
  warn: "border-amber-200 bg-amber-50 text-amber-900",
  error: "border-red-200 bg-red-50 text-red-900",
};

const defaultAlertIcons: Record<
  Exclude<AlertVariant, "warning">,
  React.ReactNode
> = {
  info: <span aria-hidden>i</span>,
  success: <span aria-hidden>✓</span>,
  warn: <span aria-hidden>!</span>,
  error: <span aria-hidden>!</span>,
};

export const Alert = ({
  variant = "info",
  title,
  icon,
  className,
  children,
  ...rest
}: AlertProps) => {
  const normalizedVariant = variant === "warning" ? "warn" : variant;
  const resolvedIcon = icon ?? defaultAlertIcons[normalizedVariant];

  return (
    <div
      role="alert"
      className={cn(
        "flex items-start gap-3 rounded-md border px-3 py-2 text-sm shadow-sm",
        alertStyles[normalizedVariant],
        className,
      )}
      {...rest}
    >
      {resolvedIcon ? (
        <div className="mt-0.5 shrink-0">{resolvedIcon}</div>
      ) : null}
      <div className="min-w-0 flex-1">
        {title ? <div className="font-semibold">{title}</div> : null}
        {children ? <div>{children}</div> : null}
      </div>
    </div>
  );
};

export const AlertDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("text-sm [&_p]:leading-relaxed", className)}
    {...props}
  />
));
AlertDescription.displayName = "AlertDescription";
